import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import camelcase from "camelcase-keys";
import { pick } from "lodash";
import { useCallback, useEffect, useMemo } from "react";
import { useMount } from "react-use";
import { STATUS_CODES } from "../../../enums";
import { TimeEnum } from "../../../lib/enums";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";
import {
  CloudSearchResponse,
  LocalSearchResponse
} from "../../../services/@types";

import { CANCEL_ERROR } from "../../../requests";
import { useReducedSystemHeathStatus } from "../../header/system-health/hooks/useSystemHealthStatus";
import { NetworkRepEntry } from "../../network/@types/Url";
import { FETCH_CLOUD_DATA, FETCH_LOCAL_DATA } from "../../samples/queryKeys";
import { SearchGridData } from "../@types";
import { getIsFirstSeenSynced } from "../components/utils";
import { DATE_FILTER, FILTER_KEYWORDS, LOCATION } from "../consts";
import {
  isCloudEnabled,
  isUsingLocalKeywordsOrSelections
} from "../containers/utils";
import {
  generateQueryWithSelections,
  isCloudDateLimitHit,
  isFuzzySimilaritySearchQuery,
  showCloudUnreachableOrNoAccessNotifications,
  transformGridData
} from "../util";
import { getFetchPeriod, isOlderThan } from "../util/cloudtotalcount";
import { queryOptions } from "./consts";
import { useCloudSearch } from "./useCloudSearch";
import useCloudTotalCount from "./useCloudTotalCount";
import { useGridConfig } from "./useGridConfig";
import { useLocalSearch } from "./useLocalSearch";
import useNetworkSearch from "./useNetworkSearch";
import useQueryValidationContext from "./useQueryValidationContext";
import useSearchContext from "./useSearchContext";
import {
  expandSearchPeriodBeyondAYear,
  expandSearchToPreviousYear,
  getSearchError,
  showCancelRequestNotification,
  switchToCloud
} from "./utils";

export interface SearchStateItem {
  count?: number;
  data?: SearchGridData | Array<NetworkRepEntry>;
  error?: ErrorResponse<MessageErrorResponse> | string | null;
  loading?: boolean;
  pageCount?: number;
  show?: boolean;
  hasExport?: boolean;
  morePages?: boolean;
}

export interface LocalSearchStateItem extends SearchStateItem {
  data?: SearchGridData;
}

export interface CloudSearchStateItem extends SearchStateItem {
  data?: SearchGridData;
  totalCloudLoading?: boolean;
}

export interface NetworkSearchStateItem extends SearchStateItem {
  data?: Array<NetworkRepEntry>;
  invalidNetworkData?: string[];
}

export interface SearchState {
  local?: LocalSearchStateItem;
  cloud?: CloudSearchStateItem;
  network?: NetworkSearchStateItem;
  notfound_hashes?: SearchStateItem;
  invalid_hashes?: SearchStateItem;
}

const { CLASSIFICATION, CLOUD_SEARCH_TYPE } = FILTER_KEYWORDS;

export const SAMPLES_REFETCH_INTERVAL = 5000;
/**
 * Hook used to retrieve search data
 * Hook combines usePrivateCloudSearch, usePublicCloudSearch, useLocalSearch and useCloudTotalCount
 * and stores returned data to state
 *
 */
const useSearch = () => {
  const { isCloudDisabled } = useReducedSystemHeathStatus();
  const queryClient = useQueryClient();
  const { expandApi, location: params, navigate } = useSearchContext();
  const { fetchCloudTotalCount, total, totalCloudLoading } =
    useCloudTotalCount();
  const { gridConfig } = useGridConfig();

  const { q, pagesize, location, selections } = params || {};
  const { period_type: { value: periodTypeValue = "" } = {} } =
    selections || {};

  const {
    data: validationData,
    mutate: validateQuery,
    isLoading: isValidating,
    error: validationError,
    abort: abortValidation
  } = useQueryValidationContext();

  const canSendToCloud = isCloudEnabled({
    query: q ?? "",
    selections: selections ?? {},
    location,
    isCloudDisabled
  });

  const cloudOnlySearchQuery = generateQueryWithSelections(
    pick(selections, [CLASSIFICATION, CLOUD_SEARCH_TYPE]),
    q
  );

  const fetchPeriod = useMemo(
    () => getFetchPeriod(periodTypeValue),
    [periodTypeValue]
  );

  const fetchTotalCount = useCallback(() => {
    if (
      periodTypeValue === TimeEnum.ALL ||
      fetchPeriod.start_search_date === "*" ||
      fetchPeriod.end_search_date === "*"
    )
      return;
    fetchCloudTotalCount(cloudOnlySearchQuery, fetchPeriod);
  }, [cloudOnlySearchQuery, periodTypeValue]);

  const olderThanAYear = isOlderThan(fetchPeriod.start_search_date, 1, "years");

  const canFetchLocationCount =
    validationData?.is_valid && !validationData.is_only_hash_or_search;

  const isFirstSeenSynced = getIsFirstSeenSynced(q ?? "", selections ?? {});

  useMount(() => {
    validateQuery(q ?? "", location).then(({ data }) => {
      if (!data?.is_valid) return;
      if (!data?.transformed_query) return;
      if (data.transformed_query !== q) {
        navigate(
          {
            ...params,
            isRecent: false,
            q: data.transformed_query
          },
          { replace: true }
        );
      }
    });
  });

  useEffect(() => {
    if (canFetchLocationCount) {
      fetchTotalCount();
    }
  }, [fetchTotalCount, canFetchLocationCount]);

  const cloudQueryIsEnabled =
    location === LOCATION.CLOUD || !!gridConfig.automatically_switch_to_cloud;

  const {
    cloudQuery: {
      data: cloudSearchData,
      isLoading: cloudIsLoading,
      isFetching: cloudIsFetching,
      isError: cloudIsError,
      error: cloudSearchError,
      refetch: refetchCloud,
      isPlaceholderData: cloudIsPlaceholder
    },
    cloudAbortController: abortCloud
  } = useCloudSearch({
    args: {
      params
    },
    options: {
      enabled:
        canSendToCloud &&
        Boolean(validationData?.is_valid) &&
        cloudQueryIsEnabled,
      onSuccess: (
        response: CloudSearchResponse & InfiniteData<CloudSearchResponse>
      ) => {
        const isCloudLocation = location === LOCATION.CLOUD;
        const { cloudSampleCount, endOfDataset } = camelcase(response);

        if (!isCloudLocation) return;

        const isLoadingDataset = !cloudSampleCount && !endOfDataset;

        if (isLoadingDataset) {
          return;
        }

        if (cloudSampleCount || isFirstSeenSynced) {
          expandApi?.stop();
          return;
        }

        if (!olderThanAYear && gridConfig.automatically_expand_search_period) {
          expandSearchToPreviousYear(navigate, params, fetchPeriod);
          return;
        }

        if (
          isOlderThan(fetchPeriod.end_search_date, 1, "years") &&
          !isCloudDateLimitHit(fetchPeriod.start_search_date) &&
          gridConfig.automatically_expand_search_period
        ) {
          expandSearchPeriodBeyondAYear(navigate, params, fetchPeriod);
          return;
        }

        if (endOfDataset) {
          expandApi?.stop();
          return;
        }
      },
      onError: error => {
        expandApi?.stop();
        if (error.problem === CANCEL_ERROR) return;
        if (
          [STATUS_CODES.FORBIDDEN, STATUS_CODES.UNAUTHORIZED].includes(
            error.status
          )
        ) {
          showCloudUnreachableOrNoAccessNotifications(null);
          return;
        }
      }
    }
  });

  const {
    localQuery: {
      data: localSearchData,
      isLoading: localIsLoading,
      isFetching: localIsFetching,
      isError: localIsError,
      error: localSearchError,
      refetch: refetchLocal,
      isPlaceholderData: localIsPlaceholderData
    },
    abortLocal
  } = useLocalSearch({
    args: {
      params
    },
    options: {
      ...queryOptions,
      refetchInterval: query => {
        if (query.state.error) return false;
        return location === LOCATION.LOCAL &&
          !isFuzzySimilaritySearchQuery(params.q)
          ? SAMPLES_REFETCH_INTERVAL
          : false;
      },
      enabled: Boolean(validationData?.is_valid),
      select: (response: AxiosResponse<LocalSearchResponse>) => response.data,
      onSuccess: (localData: LocalSearchResponse) => {
        if (location !== LOCATION.LOCAL) return;

        const { localSampleCount, transformedQuery } = camelcase(localData);

        if (localSampleCount || isFirstSeenSynced) {
          expandApi?.stop();
          return;
        }

        const onlyLocalKeywords = isUsingLocalKeywordsOrSelections(
          transformedQuery,
          params.selections,
          false
        );

        const shouldSwitchToCloud =
          !onlyLocalKeywords &&
          !isCloudDisabled &&
          !!gridConfig.automatically_switch_to_cloud;

        if (shouldSwitchToCloud) {
          switchToCloud(navigate, params);
          return;
        }

        // don't expand search when entire period selected
        if (selections?.period_type?.value === DATE_FILTER.ALL) {
          return;
        }

        if (!olderThanAYear && gridConfig.automatically_expand_search_period) {
          expandSearchToPreviousYear(navigate, params, fetchPeriod);
          return;
        }

        if (
          isOlderThan(fetchPeriod.end_search_date, 1, "years") &&
          !isCloudDateLimitHit(fetchPeriod.start_search_date) &&
          gridConfig.automatically_expand_search_period
        ) {
          expandSearchPeriodBeyondAYear(navigate, params, fetchPeriod);
          return;
        }
        expandApi?.stop();
      },
      onError: error => {
        expandApi?.stop();
        if (error.problem === CANCEL_ERROR) return;
      }
    }
  });

  const {
    data: networkSearchData,
    isFetching: networkDataIsLoading,
    totalCount: networkDataTotalCount,
    show: showNetworkSearchCount,
    invalidNetworkData,
    abortQuery: abortNetworkSearch,
    error: networkSearchError
  } = useNetworkSearch();

  // reset validation when location changes and search input has invalid query
  // this is to prevent infinite loading when switching filter tabs while
  // input query is invalid
  useEffect(() => {
    if (validationData?.is_valid) return;
    queryClient.removeQueries({ queryKey: [FETCH_CLOUD_DATA] });
    queryClient.removeQueries({ queryKey: [FETCH_LOCAL_DATA] });
  }, [queryClient, validationData?.is_valid]);

  const isLocalLoading =
    (localIsLoading && validationData?.is_valid) ||
    (localIsPlaceholderData && localIsFetching) ||
    expandApi?.isExpanding;

  const isCloudLoading =
    (cloudIsLoading && validationData?.is_valid) ||
    (cloudIsPlaceholder && cloudIsFetching) ||
    expandApi?.isExpanding;

  const isLocalError = localIsError || Boolean(validationError);
  const isCloudError = cloudIsError || Boolean(validationError);

  const localError = getSearchError(
    localSearchError,
    localSearchData?.data,
    validationError,
    validationData?.err_message
  );

  const cloudError = getSearchError(
    cloudSearchError,
    cloudSearchData?.data,
    validationError,
    validationData?.err_message
  );

  const returnData: SearchState = useMemo(
    () => ({
      local: {
        data: transformGridData(localSearchData?.data || []),
        count: !isLocalError ? localSearchData?.local_sample_count : 0,
        pageCount: Math.ceil(
          (localSearchData?.local_sample_count ?? 0) / pagesize
        ),
        loading:
          location === LOCATION.LOCAL &&
          ((isLocalLoading && !isLocalError) || isValidating),
        show: !isLocalError,
        error: localError,
        hasExport: true,
        morePages: localSearchData?.more_pages
      },
      cloud: {
        data: transformGridData(cloudSearchData?.data || []),
        count: !isCloudError ? total : 0,
        pageCount: Math.ceil(
          (cloudSearchData?.cloud_sample_count ?? 0) / pagesize
        ),
        loading:
          location === LOCATION.CLOUD &&
          ((isCloudLoading && !isCloudError) || isValidating),
        totalCloudLoading,
        show: !isCloudError ? Boolean(total) : false,
        error: cloudError,
        hasExport: true,
        morePages: cloudSearchData?.more_pages
      },
      network: {
        data: networkSearchData,
        count: networkDataTotalCount,
        show: showNetworkSearchCount,
        loading: networkDataIsLoading,
        error: networkSearchError,
        pageCount: Math.ceil(networkDataTotalCount / pagesize),
        invalidNetworkData
      }
    }),
    [
      cloudError,
      cloudSearchData?.cloud_sample_count,
      cloudSearchData?.data,
      invalidNetworkData,
      isCloudError,
      isCloudLoading,
      isLocalError,
      isLocalLoading,
      isValidating,
      localError,
      localSearchData?.data,
      localSearchData?.local_sample_count,
      location,
      networkDataIsLoading,
      networkDataTotalCount,
      networkSearchError,
      networkSearchData,
      pagesize,
      q,
      selections,
      showNetworkSearchCount,
      total,
      totalCloudLoading
    ]
  );

  const abort = useCallback(() => {
    showCancelRequestNotification();

    if (isCloudLoading) {
      abortCloud?.abort("Manual cloud abort!");
    }

    if (isLocalLoading) {
      abortLocal();
    }

    if (networkDataIsLoading) {
      abortNetworkSearch();
    }

    if (isValidating) {
      abortValidation();
    }
  }, [
    abortCloud,
    abortLocal,
    abortNetworkSearch,
    abortValidation,
    isCloudLoading,
    isLocalLoading,
    isValidating,
    networkDataIsLoading
  ]);

  return {
    refetchLocal,
    refetchCloud,
    data: returnData,
    abort
  };
};

export default useSearch;
